
import React, { useState, useEffect } from "react";
import { Separator } from "@/components/ui/separator";
import { Slack, AlertCircle } from "lucide-react";
import { useSlackIntegration } from "./slack/hooks";
import SlackConnectionForm from "./slack/SlackConnectionForm";
import SlackSettingsForm from "./slack/SlackSettingsForm";
import { SlackIntegrationProps } from "./slack/types";
import { logger } from "@/lib/logger";
import { IntegrationLoading } from "./slack/components/IntegrationLoading";
import { ConnectionError } from "./slack/components/ConnectionError";
import { AdvancedDetails } from "./slack/components/AdvancedDetails";
import IntegrationCard from "./common/IntegrationCard";

const SlackIntegration: React.FC<SlackIntegrationProps> = ({ 
  aiStaffId = "1", 
  aiStaffName = "Morgan" 
}) => {
  logger.debug({
    message: "Rendering Slack integration for staff",
    context: { aiStaffId, aiStaffName }
  });
  
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [isCheckingTimeout, setIsCheckingTimeout] = useState(false);
  
  const {
    isConnected,
    connectionError,
    webhookUrl,
    setWebhookUrl,
    slackToken,
    setSlackToken,
    triggerTag,
    handleTagChange,
    isFileProcessingEnabled,
    handleFileProcessingChange,
    isAutoResponsesEnabled,
    handleAutoResponsesChange,
    isRoleDetectionEnabled,
    handleRoleDetectionChange,
    isLoading,
    isChecking,
    isTestingConnection,
    handleConnect,
    handleDisconnect,
    testConnection
  } = useSlackIntegration(aiStaffId);

  // Add timeout to handle potential infinite "checking" state
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    if (isChecking) {
      timeout = setTimeout(() => {
        setIsCheckingTimeout(true);
      }, 10000); // 10 second timeout
    } else {
      setIsCheckingTimeout(false);
    }
    
    return () => {
      clearTimeout(timeout);
    };
  }, [isChecking]);

  const handleTestConnection = () => {
    testConnection(webhookUrl, slackToken);
  };

  // Show loading for maximum 10 seconds, then show an option to continue anyway
  if (isChecking && !isCheckingTimeout) {
    return <IntegrationLoading />;
  }

  return (
    <IntegrationCard
      title="Slack Integration"
      description={`Connect ${aiStaffName} to your Slack workspace to receive assistance directly in your channels.`}
      icon={<Slack className="h-5 w-5 text-blue-500" />}
      isConnected={isConnected}
      isChecking={isChecking && isCheckingTimeout}
      connectionError={connectionError}
    >
      {isChecking && isCheckingTimeout && (
        <div className="p-4 bg-yellow-50 rounded-md mb-4 border border-yellow-200">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Connection check is taking longer than expected</p>
              <p className="text-xs text-yellow-700 mt-1">
                This might be due to network issues or Slack API delays. You can continue with the setup.
              </p>
            </div>
          </div>
        </div>
      )}

      {connectionError && (
        <ConnectionError
          error={connectionError}
          showTroubleshooting={showTroubleshooting}
          onToggleTroubleshooting={() => setShowTroubleshooting(!showTroubleshooting)}
          onTestConnection={handleTestConnection}
        />
      )}

      <Separator className="my-4" />

      {!isConnected ? (
        <SlackConnectionForm
          webhookUrl={webhookUrl}
          setWebhookUrl={setWebhookUrl}
          slackToken={slackToken}
          setSlackToken={setSlackToken}
          isLoading={isLoading}
          onConnect={handleConnect}
        />
      ) : (
        <SlackSettingsForm
          triggerTag={triggerTag}
          onTagChange={handleTagChange}
          isFileProcessingEnabled={isFileProcessingEnabled}
          onFileProcessingChange={handleFileProcessingChange}
          isAutoResponsesEnabled={isAutoResponsesEnabled}
          onAutoResponsesChange={handleAutoResponsesChange}
          isRoleDetectionEnabled={isRoleDetectionEnabled}
          onRoleDetectionChange={handleRoleDetectionChange}
          onDisconnect={async () => {
            await handleDisconnect();
            return void 0; // Convert Promise<boolean> to Promise<void>
          }}
          aiStaffName={aiStaffName}
          hideDisconnect={false}
        />
      )}
      
      <AdvancedDetails
        aiStaffId={aiStaffId}
        webhookUrl={webhookUrl}
        slackToken={slackToken}
      />
    </IntegrationCard>
  );
};

export default SlackIntegration;
